import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { SupportFeatureFlags } from '@common/contexts/support/domain/enums/featureFlags';
import { Breakpoints, ManagerAuthorization, Profession, ServiceCode, ServiceType } from '@common/enums';
import { ActionsPermission } from '@common/enums/actionPermission';
import { PractitionerRole } from '@common/enums/practitionerRole';
import { emptyUuid } from '@common/utils/uuid';
import {
    employmentHasAccessToHolterDashboard,
    employmentHasAccessToHomePage,
    organizationHasAccessToFundusNoShows,
} from '@frontend/services/api/accessCheckApiService';
import { getOneActionPermission } from '@frontend/services/api/actionPermissionApiService';
import { getServiceEmploymentSettings } from '@frontend/services/api/serviceEmploymentsApiService';
import { hasOneAuthorization, useAbortableFetch, useViewportWidth } from '@frontend/utils/';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useFeatureFlag } from '@frontend/utils/hooks/useFeatureFlag';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import type { IEmployment, IOrganization, IServiceEmployment } from '../../interfaces/';
import type { EmploymentSwitch } from './types/employmentSwitch';
import type { GlobalUserContext } from './types/globalUserContext';
import type { UserEmployment } from './types/userEmployment';
import { DesktopHeader } from './components/DesktopHeader';
import { MobileHeader } from './components/MobileHeader';
import { useNavigationItems } from './hooks/useNavigationItems';

export function NavigationBar() {
    const abortSignal = useAbortSignal();
    const { frontendSettings } = useFrontendSettings();
    const authentication = useSelector((state: IReduxState) => state.authentication);
    const reduxLanguage = useSelector((state: IReduxState) => state.language);
    const serviceConsumersData = useSelector((state: IReduxState) => state.serviceConsumers);
    const isOutpatientClinicTaskListEnabled = useFeatureFlag(SupportFeatureFlags.EnableOutpatientClinicTaskList, false);

    const globalUserContext = useMemo<GlobalUserContext>(
        () => ({
            currentLanguage: reduxLanguage.code,
            employmentPractitionerRoles: authentication.employmentPractitionerRoles,
            hasEmploymentRights: authentication.hasEmploymentRights,
            isKsyos: authentication.isKsyos,
            isManager: authentication.isManager,
            isReferralConsumer: serviceConsumersData.data.serviceConsumers.some(
                (serviceConsumer) =>
                    (serviceConsumer.service.serviceType === ServiceType.referral ||
                        serviceConsumer.service.serviceType === ServiceType.referralSmall) &&
                    serviceConsumer.isRequestCreator
            ),
            nameFamily: authentication.nameFamily,
            nameGiven: authentication.nameGiven,
            nameInfix: authentication.nameInfix,
            organizationId: authentication.organizationId,
            profilePictureUrl: authentication.profilePictureUrl,
            selectedEmploymentId: authentication.selectedEmploymentId,
            serviceConsumers: serviceConsumersData.data.serviceConsumers,
            serviceConsumersLoadStatus: serviceConsumersData.status,
        }),
        [authentication, reduxLanguage, serviceConsumersData]
    );

    const [employments, setEmployments] = useState<UserEmployment[]>([]);
    const [currentProfession, setCurrentProfession] = useState<Profession>(Profession.other);
    const [currentOrganization, setCurrentOrganization] = useState<
        Pick<IOrganization, 'id' | 'name' | 'suppressEmployeePayoutOverview'>
    >({
        id: emptyUuid,
        name: '',
        suppressEmployeePayoutOverview: false,
    });
    const [hasAccessToHomepage, setHasAccessToHomepage] = useState(false);
    const [hasAccessToHolterDashboard, setHasAccessToHolterDashboard] = useState(false);
    const [hasAccessToFundusNoShows, setHasAccessToFundusNoShows] = useState(false);
    const [hasAccessToOutpatientClinicTaskList, setHasAccessToOutpatientClinicTaskList] = useState(false);
    const [profileUrl, setProfileUrl] = useState('');
    const [userInformation, setUserInformation] = useState(globalUserContext);
    const [paymentsUrl, setPaymentsUrl] = useState('');
    const [employmentSwitch, setEmploymentSwitch] = useState<EmploymentSwitch | null>(null);
    const abortableFetch = useAbortableFetch();
    const { width } = useViewportWidth();
    const [bariPreSettings, setBariPreSettings] = useState<IServiceEmployment['settings']>();
    const [bariPostSettings, setBariPostSettings] = useState<IServiceEmployment['settings']>();
    const [osasSettings, setOsasSettings] = useState<IServiceEmployment['settings']>();
    const isOrganizationPayoutManager = !!globalUserContext.employmentPractitionerRoles?.find(
        (practitionerRole) => practitionerRole === PractitionerRole.organizationPayoutManager
    );
    const isOnlyOrganizationPayoutManager = !globalUserContext.hasEmploymentRights && isOrganizationPayoutManager;

    const navigationItems = useNavigationItems({
        bariPostSettings,
        bariPreSettings,
        currentOrganization,
        currentProfession,
        hasAccessToFundusNoShows,
        hasAccessToHolterDashboard,
        hasAccessToHomepage,
        hasAccessToOutpatientClinicTaskList,
        isKsyos: globalUserContext.isKsyos,
        isManager: globalUserContext.isManager,
        isOnlyOrganizationPayoutManager,
        osasSettings,
        serviceConsumers: globalUserContext.serviceConsumers,
    });

    useEffect(() => {
        checkAccess();
        loadEmployments();
        loadServiceEmploymentsSettings();
    }, []);

    useEffect(() => {
        reloadCurrentUserInformation();
    }, [employments, globalUserContext.profilePictureUrl]);

    useEffect(() => {
        checkAccessHomepage();
    }, [globalUserContext.serviceConsumers, globalUserContext.serviceConsumersLoadStatus]);

    if (width < Breakpoints.small) {
        return (
            <MobileHeader
                currentOrganization={currentOrganization}
                employments={employments}
                employmentSwitch={employmentSwitch}
                frontendSettings={frontendSettings}
                globalUserContext={{ ...globalUserContext }}
                navigationItems={navigationItems}
                paymentsUrl={paymentsUrl}
                profileUrl={profileUrl}
                setEmploymentSwitch={setEmploymentSwitch}
                userInformation={userInformation}
            />
        );
    }

    return (
        <DesktopHeader
            currentOrganization={currentOrganization}
            employments={employments}
            employmentSwitch={employmentSwitch}
            frontendSettings={frontendSettings}
            globalUserContext={{ ...globalUserContext }}
            navigationItems={navigationItems}
            paymentsUrl={paymentsUrl}
            profileUrl={profileUrl}
            setEmploymentSwitch={setEmploymentSwitch}
            userInformation={userInformation}
        />
    );

    async function loadEmployments() {
        const url = '/api/user/employments?addIsReferralProvider=true&addEmploymentPractitionerRoles=true';
        const result = await abortableFetch.fetchJson(url, 'GET');
        const currentEmployment = result.json.items.find(
            (item: { id: string }) => item.id === globalUserContext.selectedEmploymentId
        );
        const paymentEmployment = employmentHasPayouts(currentEmployment)
            ? currentEmployment
            : result.json.items.find((employment: IEmployment) => employmentHasPayouts(employment));

        if (!currentEmployment) {
            return;
        }

        updateCurrentOrganization(currentEmployment.organization);
        setCurrentProfession(currentEmployment.profession);
        setProfileUrl(`/practitioners/${currentEmployment.practitionerId}/profile`);
        setEmployments(result.json.items);
        if (paymentEmployment) {
            setPaymentsUrl(`/practitioners/${currentEmployment.practitionerId}/payments`);
        }
    }

    async function reloadCurrentUserInformation() {
        if (employments.length !== 0) {
            const currentEmployment = employments.find(
                (item: { id: string }) => item.id === globalUserContext.selectedEmploymentId
            );
            const url = `/api/practitioners/${currentEmployment?.practitionerId}/?includeProfileThumbnail=true`;
            const result = await abortableFetch.fetchJson(url, 'GET');
            if (result.json) {
                setUserInformation(result.json);
            }
        }
    }

    function employmentHasPayouts(employment: IEmployment) {
        return employment.relationNumber && !employment.organization?.suppressEmployeePayoutOverview;
    }

    function updateCurrentOrganization({
        name,
        id,
        suppressEmployeePayoutOverview,
    }: Pick<IOrganization, 'id' | 'name' | 'suppressEmployeePayoutOverview'>) {
        setCurrentOrganization({
            id: id || emptyUuid,
            name: name || '...',
            suppressEmployeePayoutOverview: suppressEmployeePayoutOverview ?? false,
        });
    }

    function checkAccess() {
        checkAccessHomepage();
        checkAccessHolterDashboard();
        checkAccessFundusNoShows();
        checkAccessOutpatientClinicTaskList();
    }

    async function checkAccessHomepage() {
        const result =
            !globalUserContext.isManager &&
            !isOnlyOrganizationPayoutManager &&
            (await employmentHasAccessToHomePage(globalUserContext.selectedEmploymentId, abortSignal));

        setHasAccessToHomepage(result);
    }

    async function checkAccessHolterDashboard() {
        const result =
            !globalUserContext.isManager &&
            (await employmentHasAccessToHolterDashboard(globalUserContext.selectedEmploymentId, abortSignal));
        setHasAccessToHolterDashboard(result);
    }

    async function checkAccessFundusNoShows() {
        const result = await organizationHasAccessToFundusNoShows(globalUserContext.organizationId, abortSignal);
        setHasAccessToFundusNoShows(result || hasOneAuthorization(ManagerAuthorization.fundusScreeningManualImport));
    }

    async function checkAccessOutpatientClinicTaskList() {
        if (!isOutpatientClinicTaskListEnabled || !authentication.areContactsAndTasksEnabled) {
            return;
        }

        const { canRead } = await getOneActionPermission({
            action: ActionsPermission.PatientTask,
            signal: abortSignal,
        });

        setHasAccessToOutpatientClinicTaskList(canRead);
    }

    async function loadServiceEmploymentsSettings(): Promise<void> {
        const bariPre = await getServiceEmploymentSettings(
            globalUserContext.selectedEmploymentId,
            ServiceCode.BariPre,
            abortSignal
        );

        const bariPost = await getServiceEmploymentSettings(
            globalUserContext.selectedEmploymentId,
            ServiceCode.BariPost,
            abortSignal
        );

        const osas = await getServiceEmploymentSettings(
            globalUserContext.selectedEmploymentId,
            ServiceCode.OSAS,
            abortSignal
        );

        setOsasSettings(osas);
        setBariPreSettings(bariPre);
        setBariPostSettings(bariPost);
    }
}
